import { selectCurrentHotelId } from 'accessors'
import { PMSs } from 'main/hotelBrandsAndChains'
import { useCallback, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router'
import { pushElectronDashboardAuthSnapshot } from 'services/helpers/electronDashboardAuthSync'
import { resolveDashboardLoginStatus } from 'services/helpers/resolveDashboardLoginStatus'

const REQUEST_AUTH_SNAPSHOT = 'request-auth-snapshot'

function pushAuthSnapshotToElectron() {
  pushElectronDashboardAuthSnapshot()
}

function useElectronMessageHandler() {
  const subsidiaryId = useSelector(selectCurrentHotelId)
  const pms = useSelector(s => s.hotelDetails.pms)
  const isAppReloading = useSelector(state => state.login.isAppReloading)
  const history = useHistory()

  const hasPepPMS = pms === PMSs.pep

  const handleLoginStatusPing = useCallback(async () => {
    try {
      const res = await resolveDashboardLoginStatus()
      window.electron.postMessage({
        type: 'login-status-response',
        data: {
          loginStatus: res,
          subsidiaryId
        }
      })
    } catch (error) {
      window.electron.postMessage({
        type: 'login-status-response',
        data: {
          loginStatus: 'dead',
          error: error.message,
          subsidiaryId
        }
      })
    }
  }, [subsidiaryId])

  const handleSubsidiaryIdRequest = useCallback(async () => {
    try {
      const res = await resolveDashboardLoginStatus()
      window.electron.postMessage({
        type: 'subsidiary-id',
        data: {
          subsidiaryId: res === 'alive' ? subsidiaryId : null
        }
      })
    } catch (error) {
      window.electron.postMessage({
        type: 'subsidiary-id',
        data: {
          subsidiaryId: null
        }
      })
    }
  }, [subsidiaryId])

  const handleNavigation = useCallback(
    async target => {
      if (!hasPepPMS) return

      try {
        history.push(target)

        // Send tracking message to Electron after navigation
        if (window?.electron?.postMessage) {
          window.electron.postMessage({
            type: 'navigation-completed',
            data: {
              destination: target,
              timestamp: new Date().toISOString(),
              currentUrl: window.location.href,
              isSuccess: true
            }
          })
        }

      } catch (error) {
        // Navigation failed, send error message
        if (window?.electron?.postMessage) {
          window.electron.postMessage({
            type: 'navigation-completed',
            data: {
              destination: target,
              timestamp: new Date().toISOString(),
              currentUrl: window.location.href,
              isSuccess: false
            }
          })
        }
      }
    },
    [hasPepPMS, history]
  )

  const handler = useCallback(
    async (_, event) => {
      switch (event.type) {
        case 'PING_LOGIN_STATUS':
          await handleLoginStatusPing()
          break

        case 'GET_SUBSIDIARY_ID':
          await handleSubsidiaryIdRequest()
          break

        case 'NAVIGATE_TO_PAGE':
          await handleNavigation(event.target)
          break

        case REQUEST_AUTH_SNAPSHOT:
          pushAuthSnapshotToElectron()
          break

        default:
          break
      }
    },
    [handleLoginStatusPing, handleSubsidiaryIdRequest, handleNavigation]
  )

  // Setup and cleanup electron message listener (runs without subsidiaryId so auth snapshot works).
  useEffect(() => {
    if (isAppReloading) return

    if (window.electron && typeof window.electron.onMessage === 'function') {
      window.electron.onMessage(handler)
      return () => {
        if (typeof window.electron.offMessage === 'function') {
          window.electron.offMessage(handler)
        }
      }
    }
  }, [handler, isAppReloading])
}

export default useElectronMessageHandler
